import {
  CtaBuilderFn,
  InstantAnswerBuilderFn,
  InstantAnswerContext,
  InstantAnswerScenario,
} from "@faire/help-center-self-serve/types";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";

import { orderIsFreeShippingEligible } from "../../utils/orderIsFreeShippingEligible";

import { ViewOrderInvoiceCTA } from "./ctas";

export const DidNotGetInsiderFreeShippingIA: InstantAnswerBuilderFn = (
  strictLocalize
) => {
  return {
    titleBuilder: (context) => {
      return getAnswer(context, strictLocalize).getTitle();
    },

    descriptionBuilder: (context) => {
      return getAnswer(context, strictLocalize).getDescription();
    },

    primaryCtaBuilder: {
      textBuilder: (context) => {
        return getPrimaryCTA(context, strictLocalize)?.label;
      },
      linkBuilder: (context) => {
        return getPrimaryCTA(context, strictLocalize)?.getLink?.(
          context.brandOrder
        );
      },
    },
  };
};

const getAnswer = (
  context: InstantAnswerContext,
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => {
  const { brandOrder } = context;

  if (!brandOrder) {
    return ORDER_IS_FREE_SHIPPING_INELIGIBLE(strictLocalize);
  }

  const isFreeShippingEligible = orderIsFreeShippingEligible(brandOrder);

  if (!isFreeShippingEligible) {
    return ORDER_IS_FREE_SHIPPING_INELIGIBLE(strictLocalize);
  }

  return ORDER_IS_FREE_SHIPPING_ELIGIBLE(strictLocalize);
};

const getPrimaryCTA: CtaBuilderFn = (context, strictLocalize) => {
  const { brandOrder } = context;

  if (!brandOrder) {
    return undefined;
  }

  const isFreeShippingEligible = orderIsFreeShippingEligible(brandOrder);

  if (!isFreeShippingEligible) {
    return undefined;
  }

  return ViewOrderInvoiceCTA(strictLocalize, brandOrder);
};

const ORDER_IS_FREE_SHIPPING_INELIGIBLE = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => WHY_I_DID_NOT_RECEIVE_INSIDER_FREE_SHIPPING(strictLocalize),
  getDescription: () => [
    INSIDER_OFFERS_FREE_SHIPPING_WITH_SELECT_BRANDS(strictLocalize),
  ],
});

const ORDER_IS_FREE_SHIPPING_ELIGIBLE = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => WHY_I_DID_NOT_RECEIVE_INSIDER_FREE_SHIPPING(strictLocalize),
  getDescription: () => [
    ORDER_SHOULD_QUALIFY_FOR_INSIDER_FREE_SHIPPING(strictLocalize),
  ],
});

const WHY_I_DID_NOT_RECEIVE_INSIDER_FREE_SHIPPING = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage: "Why didn’t I receive Insider free shipping on my order?",
    description: {
      text: "Title for the instant answer of I didn't get Insider free shipping on an order issue",
    },
  });

const INSIDER_OFFERS_FREE_SHIPPING_WITH_SELECT_BRANDS = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "It’s important to note that your Insider membership provides free shipping with select brands. While this benefit isn’t applicable across the entire marketplace, you can easily identify eligible brands while browsing on Faire. Just look for brands marked with “Insider Free Shipping” along with a blue key symbol. Additionally, if your Insider membership covers the shipping cost for a particular brand, it will be indicated at checkout. If a brand is not covered, the shipping cost is displayed as “TBD”, or an estimated shipping cost is provided.",
  });

const ORDER_SHOULD_QUALIFY_FOR_INSIDER_FREE_SHIPPING = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "The order you’ve selected should qualify for Insider free shipping. To confirm if Insider free shipping has been applied to this order, go to the “Invoices” tab in your Faire portal and locate the corresponding invoice. Check for the shipping cost line, where it should clearly indicate the application of Insider free shipping to this order. If you don’t see this, please reach out to our support team for additional assistance.",
  });
